import getColorConsole from './util/console';

// can we use the perf API?
const markable = (
  // sane performance API object
  window && window.performance && window.performance.timing
    // feature detect all consumable APIs by name and type
    && (window.performance.mark && typeof window.performance.mark === 'function')
    && (window.performance.measure && typeof window.performance.measure === 'function')
    && (window.performance.getEntries && typeof window.performance.getEntries === 'function')
    && (window.performance.getEntriesByName && typeof window.performance.getEntriesByName === 'function')
    && (window.performance.getEntriesByType && typeof window.performance.getEntriesByType === 'function')
    && (window.performance.clearMeasures && typeof window.performance.clearMeasures === 'function')
);
// pointer to perf API
const perf = (markable) ? window.performance : undefined;

/**
 * @class
 */
export class PerfMarks {
  constructor(namespace, measures, gtmHelper) {
    // show console messaging
    this._debug = false;
    // keep a tally for internal perf
    this._seenMarks = [];
    // serialization char sequence
    this._ss = '::';
    // default namespace, change to differentiate traces
    this._namespace = namespace || `visa${this._ss}perf`;
    // array of raw measures
    this._measures = measures || [];
    // kvp of measures (n-end: { measure def })
    this._measuresSerialized = null;
    // pass-through to GTM util
    this._gtm = gtmHelper || null;
    // console logger
    this.clog = getColorConsole(this._debug);
  }

  get helper() {
    return this._gtm;
  }
  /**
   * expects a function/method
   * it will be called with the serialized performance measurement
   */
  set helper(o) {
    if (typeof o === 'function') {
      this._gtm = o
    } else {
      const err = `Data collection helper must be a method.`;
      throw new Error(err);
    }
  }

  set debug(b) {
    this._debug = !!b;
    this.clog = getColorConsole(this._debug);
  }

  get measures() {
    return this._measures;
  }

  set measures(a) {
    if (Array.isArray(a) && a.length > 0) {
      this._measures = a.map(m => {
        if (!m.start || !m.end) {
          this.clog('No `start` or `end` found', 'error');
          this.clog(m, 'error');
        }

        if (m.start.indexOf(this._namespace) < 0) {
          m.start = [this._namespace, m.start].join(this._ss);
        }
        if (m.end.indexOf(this._namespace) < 0) {
          m.end = [this._namespace, m.end].join(this._ss);
        }
        return m;
      });
      this._measuresSerialized = null;
    } else {
      const err = `Measures must be an array or similar with iterable measure configs.`;
      throw new Error(err);
    }
  }

  get namespace() {
    return this._namespace;
  }

  set namespace(s) {
    if (typeof s === 'string' && s.length > 0) {
      this._namespace = s;
    } else {
      const err = `Namespace must be a string and non-empty`;
      throw new Error(err);
    }
  }

  /**
   * Confirms whether a mark is a bookend for a measure defined in the preset/config
   * @param {!string} endMark
   * @returns {boolean}
   */
  isBookend = (endMark) => {
    if (this._measures.length > 0) {
      // hydrate on 1st run - flips endMark as ObjKey for quick lookup
      if (!this._measuresSerialized) {
        let startMark;
        this._measuresSerialized = {};

        this.measures.forEach(m => {
          this._measuresSerialized[m.end] = m;
        });
      }

      return (typeof this._measuresSerialized[endMark] !== 'undefined');
    }

    return false;
  }

  /**
   * Render measures into GTM-able data payload
   * @param {!boolean} clear True to clear current measurements
   * @return {Array}
   */
  getPerformanceMeasures = (clear) => {
    const ret = [];

    perf.getEntriesByType('measure').forEach(measure => {
      // other implementations may be using perf API, we only want our data
      if (measure.name.substr(0, this._namespace.length) === this._namespace) {
        ret.push({
          name      : measure.name.replace(`${this._namespace}${this._ss}`, ''),
          duration  : measure.duration,
          startTime : measure.startTime,
        });

        // callout unreasonable durations
        if (measure.duration > 60000) // 1min+
          this.clog(`${measure.name} has >1min duration!!`, 'error');

        if (clear) {
          // keep GTM event payload trim, clear to prevent duped data & noise
          perf.clearMeasures(measure.name);
        }
      }
    });

    return ret;
  }


  /**
   * @param {!string} category
   * @param {?boolean} clear
   * @param {?object} measureMeta
   */
  sendPerfMeasure = (category, clear = true, measureMeta) => {
    const measured = this.getPerformanceMeasures(clear).pop();

    // is within defined budget?
    if (measureMeta && measureMeta.budget) {
      if (measured.duration > measureMeta.budget) {
        measured.slow = true;
        measured.budget = measureMeta.budget;
        measured.overbudget = measured.duration - measureMeta.budget;
        this.clog(`${measured.name} is over its performance budget by [ ${measured.overbudget}ms ]`, 'warn');
      }
    }

    const p = {
      event          : 'PerfInstrumentation',
      event_action   : 'Measurements Record',
      event_category : 'Performance Instrumentation',
      event_label    : category,
      measurements   : measured.duration
    };
    this.clog(`@sendPerfMeasure for ${category}`, 'info');
    this.clog(p, 'info');
    this.sendGtm(p);
  }

  /**
   * Send GTM/other event
   * @param {!GtmObject} p
   */
  sendGtm = p => {
    if (this._gtm) {
      this._gtm(p);
    } else {
      this.clog(`No helper set - cannot send measurements`, 'warn');
    }
  }

  /**
   * Measures and sends to data collector (GTM typically).
   * If, and only if, `startMark` is an empty string (""),
   * this method will try to determine he match from the `endMark` name.
   * If it is any other non-truthy, it will measure from `t0`.
   * @param {!string} entryName
   * @param {?string} startMark
   * @param {!string} endMark
   * @param {?boolean} clearMeasures
   * @param {?object} measureMeta - if defined as a bookended measurement
   */
  measure = (entryName, startMark, endMark, clearMeasures = true, measureMeta) => {
    if (startMark === '' && endMark.indexOf('END') > -1) {
      startMark = endMark.replace('END', 'START');
    }

    // VCO-12695 - measure will fail if start & end mark do not exist
    let go = [startMark, endMark];
    perf.getEntriesByType('mark').forEach(m => {
      if (m.name === startMark) {
        go.shift()
      } else if (m.name === endMark) {
        go.pop();
      }
    });

    if (go.length === 0) {
      /*
        VCO-14213 - `measure` throws error with non-existant `mark`.
        Some measures reuse a mark for tN+1 - tN, SDKlite calls `mark`
      */
      try {
        perf.measure(this.ns(entryName), startMark, endMark);
        this.sendPerfMeasure(entryName, clearMeasures, measureMeta);
      } catch (ex) {
        // nuke bad marks
        perf.clearMarks(startMark);
        perf.clearMarks(endMark);
        this.clog(`[PERF] start/end mark exist in entries for ${entryName}, but cannot be measured: ${startMark}::${endMark} -- ${ex}`, 'warn');
        // send an error perfTag
        this.sendGtm({
          event          : 'PerfInstrumentation',
          event_action   : 'Measurements ERROR',
          event_category : 'Performance Instrumentation',
          event_label    : `ERROR_${entryName}`,
          event_message  : `Could not measure ${entryName} - ${ex}`,
        })
      }
    } else {
      // send an error perfTag
      this.sendGtm({
        event          : 'PerfInstrumentation',
        event_action   : 'Measurements ERROR',
        event_category : 'Performance Instrumentation',
        event_label    : `ERROR_${entryName}`,
        event_message  : `Could not measure ${entryName} - missing a start or end mark.`,
      });
    }
  }

  /**
   * Sets a mark.
   * If detected as a recordable event, 
   * the `helper` method will be called with this mark.
   * 
   * @param {!String}   s  The mark name
   * @public
   * @method
   */
  mark = (s) => {
    if (markable) {
      const isStart = s.indexOf('START') > -1;
      const isEnd = s.indexOf('END') > -1;

      // remove previous measures, if existant, to prevent false perf flags
      perf.clearMeasures(s);

      // pretty colors in console - old eyes :(
      // set the mark
      this.clog(`MARK [ ${s} ]`, isStart ? 'start' : isEnd ? 'end' : 'info');
      perf.mark(s);

      if (this.isBookend(s)) {
        const m = this._measuresSerialized[s];
        // handle explicit tN - tN+1 measures here
        this.clog(`bookending with [ ${this._measuresSerialized[s].start} ]->[ ${s} ]`);
        this.measure(m.name || s, m.start, m.end, true, m);
      } else if (isEnd) {
        // measure on END marks set
        // get bookend mark names
        let start = s.replace('END', 'START');
        // check that startMark has been made
        const startMarked = this._seenMarks.indexOf(start) > -1;
        // set start to undef if we haven't marked it
        start = (startMarked) ? start : undefined;
        if (start === undefined) this.clog(`seeing an END mark with no START [ ${s} ]`, 'warn');
        // measure and record
        this.measure(s, start, s);
      }

      // keep a running tally of marks made by this component
      this._seenMarks.push(s);
    }
  }

  /**
   * Namespaces a name if necessary.
   */
  ns = (s) => {
    if (s.substr(0, this._namespace.length) !== this._namespace) {
      return [this._namespace, s].join(this._ss);
    }
    return s;
  }

  /**
   * Convenience method to namespace a mark
   * @param {!string} s
   */
  nsmark = (s) => {
    return this.mark(this.ns(s));
  }

  /**
   * Convenience method to namespace a measure
   * @param {!string} eventName
   * @param {?string} markStart Optional
   * @param {!string} markEnd 
   */
  nsmeasure = (eventName, markStart, markEnd) => {
    if (eventName && markEnd) {
      this.measure(
        this.ns(eventName),
        markStart ? this.ns(markStart) : null,
        this.ns(markEnd),
        true
      );
    }
  }
}
